import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Room, RoomEvent, DisconnectReason, type RemoteParticipant } from 'livekit-client';
import { useAppStore, nameOf } from '../store/useAppStore.js';
import { RTC_CONFIG, pairedRoomName } from '../lib/livekit.js';
import { sanitizeTopic } from '../lib/topic.js';
import type { LobbySignal } from '@aslgo/shared';
import { requestToken, queueReady, queueStandby } from '../api/client.js';
import { showIncoming, dismissIncoming } from '../lib/notifications.js';
import { instrumentRoom } from '../debug/instrumentRoom.js';

const LOBBY_ROOM = 'aslgo-lobby';

/** Human-readable stand-in for a peer uuid, for error strings and OS
 *  notifications. Prefers the name LiveKit carries on the participant's token —
 *  it is minted per join, so it is fresher than the roster, which only reloads
 *  at sign-in/bootstrap and therefore misses anyone who registered while this
 *  page was open. Falls back to the roster (for the paths that hold an id but
 *  no participant), then to "Someone" — never the uuid. Reads the roster through
 *  getState() because these run inside LiveKit event handlers, outside render. */
function peerLabel(peerId: string, participant?: { name?: string }): string {
  return participant?.name || nameOf(useAppStore.getState().users, peerId) || 'Someone';
}

export interface LobbyApi {
  /** Auth uuids of the other people in the lobby room — keys, not labels. */
  participants: string[];
  /** The subset of `participants` whose LiveKit `ready` attribute is '1' —
   *  the attribute /api/queue/ready sets and /api/queue/next filters on. A
   *  guide on Standby is in `participants` but not here; the learner's
   *  "guides online" gate must use this list, since the queue never offers a
   *  Standby guide. Role is still the roster's call (see LobbyView). */
  readyParticipants: string[];
  pendingPeer: string | null;
  /** `peer` is the callee's auth uuid. */
  initiateCall(peer: string, durationMinutes: number, topic?: string): void;
  /** Abort a call the learner started before the guide answered: notify the
   *  peer (dismisses their ring) and clear the pending state. */
  cancelCall(): void;
  acceptIncoming(): Promise<void>;
  declineIncoming(): void;
  error: string | null;
  /** True after LiveKit kicked this tab because the same account joined the
   *  lobby from another device (DUPLICATE_IDENTITY). Not an error: the lobby
   *  stays disconnected until the user explicitly takes over. */
  signedInElsewhere: boolean;
  /** Explicit takeover after `signedInElsewhere`: rejoin the lobby with a
   *  fresh token, which in turn kicks the other device. */
  useThisDevice(): void;
  /** Guide availability: true = in the call queue, false = on standby. */
  ready: boolean;
  /** Toggle guide availability. Updates the store flag and the server-side
   *  ready attribute on the lobby participant. */
  setReady(next: boolean): void;
}

export const LobbyContext = createContext<LobbyApi | null>(null);

export function useLobby(): LobbyApi {
  const v = useContext(LobbyContext);
  if (!v) throw new Error('useLobby must be used inside <LobbyProvider>');
  return v;
}

interface ProviderProps { children: ReactNode }

export function LobbyProvider({ children }: ProviderProps) {
  // NOTE: deliberately do NOT subscribe to `incomingFrom` here. It changes
  // every time an invite arrives, and including it in any way that ends up
  // in the connect-effect dep list causes the lobby Room to tear down and
  // reconnect mid-call-setup — which the caller sees as
  // "<peer> left before answering". The disconnect handler reads incomingFrom
  // from the store via getState() instead.
  const view = useAppStore((s) => s.view);
  const userId = useAppStore((s) => s.userId);
  const setIncoming = useAppStore((s) => s.setIncoming);
  const setCallDuration = useAppStore((s) => s.setCallDuration);
  const setCallPeer = useAppStore((s) => s.setCallPeer);
  const setView = useAppStore((s) => s.setView);
  const guideReady = useAppStore((s) => s.guideReady);
  const setGuideReady = useAppStore((s) => s.setGuideReady);

  const roomRef = useRef<Room | null>(null);
  const pendingRef = useRef<{ peer: string; callRoom: string } | null>(null);
  const [participants, setParticipants] = useState<string[]>([]);
  const [readyParticipants, setReadyParticipants] = useState<string[]>([]);
  const [pendingPeer, setPendingPeer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signedInElsewhere, setSignedInElsewhere] = useState(false);
  // Bumped by useThisDevice() to re-run the connect effect on demand.
  const [connectAttempt, setConnectAttempt] = useState(0);

  const needsLobby = (view === 'lobby' || view === 'incoming') && !!userId;

  // One snapshot feeds both lists so they can never disagree about who is in
  // the room. Called on connect and on every join / leave / attribute change.
  const refreshParticipants = (room: Room) => {
    const remote = Array.from(room.remoteParticipants.values());
    setParticipants(remote.map((p) => p.identity));
    setReadyParticipants(remote.filter((p) => p.attributes?.ready === '1').map((p) => p.identity));
  };

  useEffect(() => {
    if (!needsLobby) return;
    let cancelled = false;
    (async () => {
      try {
        const tok = await requestToken({ room: LOBBY_ROOM });
        if (cancelled) return;
        const room = new Room({ adaptiveStream: true, dynacast: true });
        instrumentRoom(room, 'lobby');

        room.on(RoomEvent.ParticipantConnected, () => { refreshParticipants(room); });
        // Ready/Standby flips arrive as attribute changes on the guide's
        // lobby participant (set server-side by /api/queue/ready|standby).
        room.on(RoomEvent.ParticipantAttributesChanged, () => { refreshParticipants(room); });
        room.on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
          refreshParticipants(room);
          if (pendingRef.current?.peer === p.identity) {
            pendingRef.current = null;
            setPendingPeer(null);
            // A label, not a key: resolve the name off the roster so a raw uuid
            // can never reach the screen.
            setError(`${peerLabel(p.identity, p)} left before answering`);
          }
          if (useAppStore.getState().incomingFrom === p.identity) {
            setIncoming(null);
            dismissIncoming();
            if (useAppStore.getState().view === 'incoming') setView('lobby');
          }
        });
        // Every client joins the lobby as its auth uuid, so a second device on
        // the same account makes LiveKit kick this one. Last device wins and
        // takeover is explicit (matches iOS): no auto-rejoin, which would kick
        // the other device straight back and re-post /api/queue/ready each
        // time (dropping the guide to the back of the queue). The local
        // guideReady flag is left alone: Ready does NOT follow the user across
        // devices — the backend reads the `ready` attribute of whichever
        // device holds the lobby, and this tab re-posts its own Ready only on
        // its own rejoin (useThisDevice → connect effect above).
        room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
          if (cancelled || reason !== DisconnectReason.DUPLICATE_IDENTITY) return;
          roomRef.current = null;
          pendingRef.current = null;
          setPendingPeer(null);
          setParticipants([]);
          setReadyParticipants([]);
          setSignedInElsewhere(true);
          if (useAppStore.getState().incomingFrom) {
            setIncoming(null);
            dismissIncoming();
            if (useAppStore.getState().view === 'incoming') setView('lobby');
          }
        });
        room.on(RoomEvent.DataReceived, (payload, participant) => {
          let msg: LobbySignal;
          try {
            msg = JSON.parse(new TextDecoder().decode(payload)) as LobbySignal;
          } catch { return; }
          if (msg.to !== userId) return;

          if (msg.type === 'invite') {
            const s = useAppStore.getState();
            if (!pendingRef.current && !s.incomingFrom) {
              const topic = sanitizeTopic(msg.topic) ?? null;
              // `incomingFrom` stays the caller's uuid (the key); the label
              // travels alongside it so IncomingView can show a name even for a
              // caller who registered after our roster last loaded. The OS
              // notification is plain text, so it takes the label directly.
              const label = peerLabel(msg.from, participant);
              setIncoming(msg.from, msg.durationMinutes ?? null, topic, participant?.name || null);
              setView('incoming');
              showIncoming(label, topic);
            }
          } else if (msg.type === 'accept' && pendingRef.current?.peer === msg.from) {
            const peer = pendingRef.current.peer;
            pendingRef.current = null;
            setPendingPeer(null);
            setCallPeer(peer);
            setView('call');
          } else if (msg.type === 'decline' && pendingRef.current?.peer === msg.from) {
            const peer = pendingRef.current.peer;
            pendingRef.current = null;
            setPendingPeer(null);
            setError(`${peerLabel(peer, participant)} declined`);
          } else if (msg.type === 'cancel') {
            // The caller aborted before we answered. Dismiss our ring and drop
            // back to the lobby — mirrors the disconnect cleanup above.
            if (useAppStore.getState().incomingFrom === msg.from) {
              setIncoming(null);
              dismissIncoming();
              if (useAppStore.getState().view === 'incoming') setView('lobby');
            }
          }
        });

        await room.connect(tok.wsUrl, tok.token, { rtcConfig: RTC_CONFIG });
        if (cancelled) { await room.disconnect(); return; }
        roomRef.current = room;
        refreshParticipants(room);
        // If the guide was Ready before this (re)connect — e.g. returning from a
        // call — re-assert it so they rejoin the queue at the bottom.
        if (useAppStore.getState().guideReady) { void queueReady(); }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      const r = roomRef.current;
      roomRef.current = null;
      pendingRef.current = null;
      setParticipants([]);
      setReadyParticipants([]);
      setPendingPeer(null);
      r?.disconnect().catch(() => {});
    };
  }, [needsLobby, userId, connectAttempt, setIncoming, setCallDuration, setCallPeer, setView]);

  const api = useMemo<LobbyApi>(() => ({
    participants,
    readyParticipants,
    pendingPeer,
    error,
    signedInElsewhere,
    useThisDevice() {
      setSignedInElsewhere(false);
      setConnectAttempt((n) => n + 1);
    },
    initiateCall(peer, durationMinutes, topic) {
      const room = roomRef.current;
      if (!room || !userId) return;
      const callRoom = pairedRoomName(userId, peer);
      pendingRef.current = { peer, callRoom };
      setPendingPeer(peer);
      setCallDuration(durationMinutes);
      const sig: LobbySignal = { type: 'invite', from: userId, to: peer, room: callRoom, durationMinutes };
      const clean = sanitizeTopic(topic);
      if (clean) sig.topic = clean;
      room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(sig)), { reliable: true });
    },
    cancelCall() {
      const pending = pendingRef.current;
      if (!pending || !userId) return;
      // Tell the guide we've backed out so their incoming screen dismisses
      // immediately (they'd otherwise ring until the 30 s auto-decline). Clear
      // local pending regardless of whether the lobby room is still up.
      const room = roomRef.current;
      if (room) {
        const sig: LobbySignal = { type: 'cancel', from: userId, to: pending.peer, room: pending.callRoom };
        room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(sig)), { reliable: true });
      }
      pendingRef.current = null;
      setPendingPeer(null);
    },
    async acceptIncoming() {
      const room = roomRef.current;
      const { incomingFrom: peer, incomingDurationMinutes } = useAppStore.getState();
      if (!room || !userId || !peer) return;
      const callRoom = pairedRoomName(userId, peer);
      const sig: LobbySignal = { type: 'accept', from: userId, to: peer, room: callRoom };
      room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(sig)), { reliable: true });
      // Give the reliable channel a beat to flush before tearing down lobby.
      await new Promise((r) => setTimeout(r, 300));
      setIncoming(null);
      dismissIncoming();
      setCallDuration(incomingDurationMinutes);
      setCallPeer(peer);
      setView('call');
    },
    declineIncoming() {
      const room = roomRef.current;
      const peer = useAppStore.getState().incomingFrom;
      if (!room || !userId || !peer) return;
      const sig: LobbySignal = { type: 'decline', from: userId, to: peer, room: pairedRoomName(userId, peer) };
      room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(sig)), { reliable: true });
      setIncoming(null);
      dismissIncoming();
      setView('lobby');
    },
    ready: guideReady,
    setReady(next) {
      setGuideReady(next);
      if (next) { void queueReady(); } else { void queueStandby(); }
    },
  }), [participants, readyParticipants, pendingPeer, error, signedInElsewhere, userId, setIncoming, setCallDuration, setCallPeer, setView, guideReady, setGuideReady]);

  return <LobbyContext.Provider value={api}>{children}</LobbyContext.Provider>;
}
